import { useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { Crown, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPremiumPromo() {
  const { authFetch } = useAdmin();
  const [cfg, setCfg] = useState({
    active: false, title: '',
    subtitle: '',
    slot_count: 100, duration_days: 30, grant_premium_days: 30,
  });
  const [grantsUsed, setGrantsUsed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${API}/admin/premium-promo`);
      if (r.ok) {
        const d = await r.json();
        setCfg(prev => ({ ...prev, ...d }));
        setGrantsUsed(d.grants_used || 0);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function save() {
    setSaving(true);
    try {
      const body = {
        active: !!cfg.active,
        title: cfg.title || null,
        subtitle: cfg.subtitle || null,
        slot_count: Math.max(1, +cfg.slot_count || 100),
        duration_days: Math.max(1, +cfg.duration_days || 30),
        grant_premium_days: Math.max(1, +cfg.grant_premium_days || 30),
      };
      const r = await authFetch(`${API}/admin/premium-promo`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (r.ok) { toast.success('Spremljeno'); load(); }
      else toast.error('Greška');
    } finally { setSaving(false); }
  }

  if (loading) return <Loader2 className="m-10 animate-spin text-zinc-500" />;

  const remaining = Math.max(0, (+cfg.slot_count || 0) - grantsUsed);

  return (
    <div className="p-4 md:p-6 max-w-3xl" data-testid="admin-premium-promo">
      <div className="flex items-center gap-3 mb-5">
        <Crown className="text-amber-500" size={28} />
        <div>
          <h1 className="text-2xl font-bold">Premium promocija</h1>
          <p className="text-sm text-zinc-400">Aktivirajte kampanju gdje prvih N registriranih dobiva premium besplatno.</p>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-5 space-y-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={!!cfg.active}
                 onChange={e => setCfg({ ...cfg, active: e.target.checked })}
                 data-testid="promo-active-toggle"
                 className="w-4 h-4 accent-amber-500"/>
          <span className="font-bold text-white">Kampanja aktivna</span>
        </label>

        <Field label="Naslov bannera"
               value={cfg.title || ''}
               testId="promo-title"
               onChange={v => setCfg({ ...cfg, title: v })}
               placeholder="Prvih 100 registriranih dobiva 1 mjesec premium besplatno!" />

        <Field label="Podnaslov"
               value={cfg.subtitle || ''}
               testId="promo-subtitle"
               onChange={v => setCfg({ ...cfg, subtitle: v })}
               placeholder="Brzo se prijavi i otključaj premium sadržaj bez plaćanja." />

        <div className="grid sm:grid-cols-3 gap-3">
          <NumField label="Broj mjesta" value={cfg.slot_count} testId="promo-slot-count"
                    onChange={v => setCfg({ ...cfg, slot_count: v })} />
          <NumField label="Trajanje kampanje (dana)" value={cfg.duration_days} testId="promo-duration"
                    onChange={v => setCfg({ ...cfg, duration_days: v })} />
          <NumField label="Premium dana po korisniku" value={cfg.grant_premium_days} testId="promo-grant-days"
                    onChange={v => setCfg({ ...cfg, grant_premium_days: v })} />
        </div>

        <div className="grid grid-cols-3 gap-3 text-center pt-2 border-t border-zinc-800">
          <Stat label="Iskorišteno" value={grantsUsed} color="amber" testId="promo-stat-used" />
          <Stat label="Preostalo" value={remaining} color="emerald" testId="promo-stat-remaining" />
          <Stat label="Vrijedi do" value={(cfg.ends_at || '').slice(0, 10) || '—'} color="sky" testId="promo-stat-ends" />
        </div>

        <button onClick={save} disabled={saving}
                data-testid="save-promo-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-600 hover:bg-amber-700 rounded text-sm font-bold disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Spremi
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 block mb-1">{label}</label>
      <input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
             data-testid={testId}
             className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
    </div>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs text-zinc-400 block mb-1">{label}</label>
      <input type="number" value={value || 0} onChange={e => onChange(+e.target.value)}
             data-testid={testId}
             className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm" />
    </div>
  );
}

function Stat({ label, value, color, testId }) {
  const cls = {
    amber: 'text-amber-400', emerald: 'text-emerald-400', sky: 'text-sky-400',
  }[color] || 'text-white';
  return (
    <div data-testid={testId}>
      <p className={`text-2xl font-bold ${cls}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
    </div>
  );
}
